import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Slide,
} from '@mui/material';
import {
  Close,
  ArrowForward,
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { getColors, createTypographyStyle, glassStyles } from '../../tokens/designTokens';
import CustomButton from '../widgets/CustomButton';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface LoginPromptBottomSheetProps {
  open: boolean;
  onClose: () => void;
  onLogin: () => void;
  shareType: 'movie' | 'watchlist' | 'profile';
  itemTitle: string;
  sharedBy?: string;
}

const LoginPromptBottomSheet: React.FC<LoginPromptBottomSheetProps> = ({
  open,
  onClose,
  onLogin,
  shareType,
  itemTitle,
  sharedBy,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const colors = getColors(theme.palette.mode === 'dark');

  const getShareDescription = () => {
    switch (shareType) {
      case 'movie':
        return `Check out this movie and discover more films to watch`;
      case 'watchlist':
        return `Browse this curated collection of movies and create your own watchlists`;
      case 'profile':
        return `See what movies they're watching and discover new favorites`;
      default:
        return `Join to access shared content and discover great movies`;
    }
  };

  if (isMobile) {
    // Mobile Bottom Sheet
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        maxHeight="70vh"
      >
        {/* Header */}
        <BottomSheetDraggableHeader sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
          pb: 2,
        }}>
          <Typography variant="h6" sx={{
            fontSize: 18,
            fontWeight: 600,
            color: colors.text.primary,
          }}>
            Join Watchlists
          </Typography>

          <IconButton
            onClick={onClose}
            sx={{
              color: colors.text.secondary,
              '&:hover': { bgcolor: colors.ui.hover },
            }}
          >
            <Close />
          </IconButton>
        </BottomSheetDraggableHeader>

        <Box sx={{ px: 3, pb: 4 }}>
          {/* Content */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 2,
          }}>
            {/* Title and Shared By */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{
                fontWeight: 600,
                color: colors.text.primary,
                mb: 0.5,
                fontSize: 18,
              }}>
                {sharedBy ? `${sharedBy} shared` : 'Someone shared'}
              </Typography>

              <Typography variant="h5" sx={{
                fontWeight: 700,
                color: colors.text.primary,
                mb: 1,
                fontSize: 20,
              }}>
                "{itemTitle}"
              </Typography>

              <Typography variant="body2" sx={{
                color: colors.text.secondary,
                fontSize: 14,
              }}>
                {getShareDescription()}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              width: '100%',
              maxWidth: 300,
              mt: 2,
              pb: 'env(safe-area-inset-bottom, 16px)',
            }}>
              <CustomButton
                onClick={onLogin}
                semantic="brand"
                variant="contained"
                size="medium"
                icon={<ArrowForward />}
                sx={{
                  width: '100%',
                  py: 1.5,
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                Sign In to Continue
              </CustomButton>

              <Button
                onClick={onClose}
                variant="text"
                sx={{
                  color: colors.text.secondary,
                  textTransform: 'none',
                  fontSize: 14,
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: colors.ui.hover,
                  },
                }}
              >
                Maybe later
              </Button>
            </Box>
          </Box>
        </Box>
      </BottomSheet>
    );
  }

  // Desktop Dialog
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          ...glassStyles.popup(theme.palette.mode === 'dark'),
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontWeight: 600,
        color: colors.text.primary,
        ...createTypographyStyle('lg', 'semibold'),
      }}>
        Join Watchlists

        <IconButton
          onClick={onClose}
          sx={{
            color: colors.text.secondary,
            '&:hover': { bgcolor: colors.ui.hover },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Content */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 2,
          py: 2,
        }}>
          {/* Title and Shared By */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              color: colors.text.primary,
              mb: 0.5,
              fontSize: 18,
            }}>
              {sharedBy ? `${sharedBy} shared` : 'Someone shared'}
            </Typography>

            <Typography variant="h5" sx={{
              fontWeight: 700,
              color: colors.text.primary,
              mb: 1,
              fontSize: 20,
            }}>
              "{itemTitle}"
            </Typography>

            <Typography variant="body2" sx={{
              color: colors.text.secondary,
              fontSize: 14,
            }}>
              {getShareDescription()}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            width: '100%',
            maxWidth: 300,
          }}>
            <CustomButton
              onClick={onLogin}
              semantic="brand"
              variant="contained"
              size="medium"
              icon={<ArrowForward />}
              sx={{
                width: '100%',
                py: 1.5,
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Sign In to Continue
            </CustomButton>

            <Button
              onClick={onClose}
              variant="text"
              sx={{
                color: colors.text.secondary,
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 500,
                '&:hover': {
                  bgcolor: colors.ui.hover,
                },
              }}
            >
              Maybe later
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LoginPromptBottomSheet; 
 